
import { Component, Vue } from 'vue-property-decorator'
import request from '@/utils/request'
import XLSX from 'xlsx'
import { saveAs } from 'file-saver'

@Component({ name: 'KpiAnalytics' })
export default class extends Vue {
  private activeTab = 'devices'

  get subsGroupedByPeriod(): any[] {
    const series = this.subsData.timeSeries || []
    const map = new Map<string, any>()
    for (const row of series) {
      const key = this.formatPeriod(row.period, this.subsGroupBy)
      if (!map.has(key)) map.set(key, { period: key, total: 0, ios: 0, android: 0, smart_map: 0, voice_assistant: 0, weassist: 0, other: 0 })
      const entry = map.get(key)!
      entry.total += row.count
      if (row.platform === 'ios') entry.ios += row.count
      else entry.android += row.count
      const fg = row.featureGroup || 'other'
      if (entry[fg] !== undefined) entry[fg] += row.count
      else entry.other += row.count
    }
    return Array.from(map.values())
  }

  private featureLabel(key: string): string {
    const labels: Record<string, string> = {
      smart_map: 'Smart Map',
      voice_assistant: 'Voice Assistant',
      weassist: 'WeASSIST',
      cane_basic: 'Smart Cane',
      other: 'Other'
    }
    return labels[key] || key
  }

  private featureTagType(key: string): string {
    const types: Record<string, string> = {
      smart_map: 'warning',
      voice_assistant: 'success',
      weassist: '',
      other: 'info'
    }
    return types[key] || 'info'
  }

  get appleReviewCount(): number {
    return (this.ratingsData.apple?.recentReviews || []).length
  }

  get googleReviewCount(): number {
    return (this.ratingsData.google?.recentReviews || []).length
  }

  get allReviews(): any[] {
    const reviews: any[] = []
    for (const r of (this.ratingsData.apple?.recentReviews || [])) {
      reviews.push({ store: 'App Store', rating: r.rating, country: r.territory, date: r.date, title: r.title, body: r.body, reviewer: r.reviewerNickname })
    }
    for (const r of (this.ratingsData.google?.recentReviews || [])) {
      reviews.push({ store: 'Google Play', rating: r.rating, country: '', date: r.date, title: '', body: r.text, reviewer: r.reviewerNickname })
    }
    reviews.sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    return reviews
  }

  get ratingsTableData(): any[] {
    const reviews = this.allReviews
    if (!reviews.length) return []
    const map = new Map<string, any>()
    for (const r of reviews) {
      const dateStr = r.date || new Date().toISOString()
      const period = this.formatPeriod(dateStr, this.ratingsGroupBy)
      const country = r.country || '?'
      const key = period + '|' + country
      if (!map.has(key)) {
        map.set(key, { key, period, country, sortDate: dateStr, count: 0, totalRating: 0, avgRating: 0, appStoreCount: 0, googleCount: 0, ratingDist: {} as Record<number, number>, reviews: [] })
      }
      const entry = map.get(key)!
      entry.count++
      entry.totalRating += (r.rating || 0)
      if (r.store === 'App Store') entry.appStoreCount++
      else entry.googleCount++
      entry.ratingDist[r.rating] = (entry.ratingDist[r.rating] || 0) + 1
      entry.reviews.push(r)
    }
    for (const entry of map.values()) {
      entry.avgRating = entry.count > 0 ? Math.round(entry.totalRating / entry.count * 10) / 10 : 0
    }
    return Array.from(map.values()).sort((a, b) => (b.sortDate || '').localeCompare(a.sortDate || ''))
  }

  // Devices
  private devicesDateRange: string[] = []
  private devicesGroupBy = 'quarter'
  private devicesData: any = {}
  private devicesLoading = false

  // Activation
  private activationDateRange: string[] = []
  private activationGroupBy = 'month'
  private activationData: any = {}
  private activationLoading = false

  // Subscriptions
  private subsDateRange: string[] = []
  private subsGroupBy = 'month'
  private subsPlatform = 'all'
  private subsData: any = {}
  private subsLoading = false

  // Ratings
  private ratingsData: any = {}
  private ratingsLoading = false
  private ratingsGroupBy = 'month'

  // OTA
  private otaDateRange: string[] = []
  private otaGroupBy = 'week'
  private otaData: any = {}
  private otaLoading = false

  // OTA Versions
  private otaVersionDateRange: string[] = []
  private otaVersionData: any = {}
  private otaVersionLoading = false

  // Downloads
  private downloadsDateRange: string[] = []
  private downloadsGroupBy = 'month'
  private downloadsData: any = {}
  private downloadsLoading = false

  // Export
  private exportDateRange: string[] = []
  private exportLoading = false

  created() {
    this.fetchDevices()
    this.fetchRatings()
  }

  private formatPeriod(period: string, groupBy: string): string {
    if (!period) return '-'
    const d = new Date(period)
    if (groupBy === 'quarter') {
      const q = Math.ceil((d.getMonth() + 1) / 3)
      return `Q${q} ${d.getFullYear()}`
    }
    if (groupBy === 'year') return String(d.getFullYear())
    if (groupBy === 'week') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  private async fetchDevices() {
    this.devicesLoading = true
    try {
      const params: any = { groupBy: this.devicesGroupBy }
      if (this.devicesDateRange?.length === 2) {
        params.startDate = this.devicesDateRange[0]
        params.endDate = this.devicesDateRange[1]
      }
      const resp = await request.get('/analytics/unique-connected-devices', { params })
      this.devicesData = resp.data?.data || {}
    } catch (e) { console.error(e) }
    this.devicesLoading = false
  }

  private async fetchActivation() {
    this.activationLoading = true
    try {
      const params: any = { groupBy: this.activationGroupBy }
      if (this.activationDateRange?.length === 2) {
        params.startDate = this.activationDateRange[0]
        params.endDate = this.activationDateRange[1]
      }
      const resp = await request.get('/analytics/activation-success-rate', { params })
      this.activationData = resp.data?.data || {}
    } catch (e) { console.error(e) }
    this.activationLoading = false
  }

  private async fetchSubscriptions() {
    this.subsLoading = true
    try {
      const params: any = { groupBy: this.subsGroupBy, platform: this.subsPlatform }
      if (this.subsDateRange?.length === 2) {
        params.startDate = this.subsDateRange[0]
        params.endDate = this.subsDateRange[1]
      }
      const resp = await request.get('/analytics/new-subscriptions', { params })
      this.subsData = resp.data?.data || {}
    } catch (e) { console.error(e) }
    this.subsLoading = false
  }

  private async fetchRatings() {
    this.ratingsLoading = true
    try {
      const resp = await request.get('/analytics/store-ratings')
      this.ratingsData = resp.data?.data || {}
    } catch (e) { console.error(e) }
    this.ratingsLoading = false
  }

  private async fetchOta() {
    this.otaLoading = true
    try {
      const params: any = { groupBy: this.otaGroupBy }
      if (this.otaDateRange?.length === 2) {
        params.startDate = this.otaDateRange[0]
        params.endDate = this.otaDateRange[1]
      }
      const resp = await request.get('/analytics/ota-connection-ratio', { params })
      this.otaData = resp.data?.data || {}
    } catch (e) { console.error(e) }
    this.otaLoading = false
  }

  private async fetchOtaVersions() {
    this.otaVersionLoading = true
    try {
      const params: any = {}
      if (this.otaVersionDateRange?.length === 2) {
        params.startDate = this.otaVersionDateRange[0]
        params.endDate = this.otaVersionDateRange[1]
      }
      const resp = await request.get('/analytics/ota-version-distribution', { params })
      this.otaVersionData = resp.data?.data || {}
    } catch (e) { console.error(e) }
    this.otaVersionLoading = false
  }

  get downloadsPeriodLabel() {
    return ({ day: 'Day', week: 'Week (Mon)', month: 'Month', quarter: 'Quarter' } as Record<string, string>)[this.downloadsGroupBy] || 'Period'
  }

  private async fetchDownloads(refresh = false) {
    this.downloadsLoading = true
    try {
      const params: any = { groupBy: this.downloadsGroupBy }
      if (refresh === true) params.refresh = 'true'
      if (this.downloadsDateRange?.length === 2) {
        params.startDate = this.downloadsDateRange[0]
        params.endDate = this.downloadsDateRange[1]
      }
      const resp = await request.get('/analytics/app-downloads', { params })
      this.downloadsData = resp.data?.data || {}
    } catch (e) { console.error(e) }
    this.downloadsLoading = false
  }

  // --------------- New Registrations tab state ---------------
  private regDateRange: string[] = this.defaultRegRange('7d')
  private regPreset = '7d'
  private regSearch = ''
  private regLoginType = ''
  private regCountry = ''
  private regData: any = { rows: [], total: 0, summary: { byCountry: [], byPlatform: [], byDay: [] }, filters: {} }
  private regLoading = false
  private regExporting = false
  private regPage = 1
  private regPageSize = 50

  private defaultRegRange(preset: string): string[] {
    const today = new Date()
    const iso = (d: Date) => d.toISOString().substring(0, 10)
    const end = iso(today)
    let start = end
    if (preset === 'today') { start = end } else if (preset === 'yesterday') {
      const y = new Date(today); y.setUTCDate(y.getUTCDate() - 1); start = iso(y)
      return [start, start]
    } else if (preset === '7d') { const s = new Date(today); s.setUTCDate(s.getUTCDate() - 6); start = iso(s) } else if (preset === '30d') { const s = new Date(today); s.setUTCDate(s.getUTCDate() - 29); start = iso(s) } else if (preset === 'mtd') { start = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-01` }
    return [start, end]
  }

  private applyRegPreset(preset: string) {
    this.regDateRange = this.defaultRegRange(preset)
    this.regPage = 1
    this.fetchRegistrations()
  }

  private pickRegCountry(country: string) {
    this.regCountry = country && country.toLowerCase() === 'unknown' ? '' : country
    this.regPage = 1
    this.fetchRegistrations()
  }

  private clearRegCountry() {
    this.regCountry = ''
    this.regPage = 1
    this.fetchRegistrations()
  }

  private pickRegDay(row: { day: string, count: number }) {
    if (!row?.day) return
    this.regDateRange = [row.day, row.day]
    this.regPreset = ''
    this.regPage = 1
    this.fetchRegistrations()
  }

  private regPlatformCount(platform: 'ios' | 'android' | 'unknown'): number {
    const row = (this.regData.summary?.byPlatform || []).find((p: any) => p.platform === platform)
    return row?.count ?? 0
  }

  private formatDateTime(value: string | Date | undefined): string {
    if (!value) return '—'
    const d = typeof value === 'string' ? new Date(value) : value
    if (Number.isNaN(d.getTime())) return '—'
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  }

  private onRegPageSizeChange(size: number) {
    this.regPageSize = size
    this.regPage = 1
    this.fetchRegistrations()
  }

  private async fetchRegistrations() {
    this.regLoading = true
    try {
      const [startDate, endDate] = this.regDateRange?.length === 2 ? this.regDateRange : this.defaultRegRange('7d')
      const params: any = {
        startDate,
        endDate,
        limit: this.regPageSize,
        offset: (this.regPage - 1) * this.regPageSize
      }
      if (this.regCountry?.trim()) params.country = this.regCountry.trim()
      if (this.regLoginType) params.loginType = this.regLoginType
      if (this.regSearch?.trim()) params.search = this.regSearch.trim()
      const resp = await request.get('/analytics/new-registrations', { params })
      this.regData = resp.data?.data || { rows: [], total: 0, summary: { byCountry: [] } }
    } catch (e: any) {
      console.error(e)
      this.$message.error(e.response?.data?.status?.message || 'Failed to fetch new registrations')
    }
    this.regLoading = false
  }

  private async exportRegistrations() {
    this.regExporting = true
    try {
      const [startDate, endDate] = this.regDateRange?.length === 2 ? this.regDateRange : this.defaultRegRange('7d')
      const params: any = { startDate, endDate }
      if (this.regCountry?.trim()) params.country = this.regCountry.trim()
      if (this.regLoginType) params.loginType = this.regLoginType
      if (this.regSearch?.trim()) params.search = this.regSearch.trim()
      const resp = await request.get('/analytics/new-registrations/export', { params, responseType: 'blob' })
      saveAs(resp.data as Blob, `new_registrations_${startDate}_${endDate}.xlsx`)
    } catch (e: any) {
      console.error(e)
      this.$message.error('Export failed')
    }
    this.regExporting = false
  }

  private handleTabChange() {
    if (this.activeTab === 'activation' && !this.activationData.timeSeries) this.fetchActivation()
    if (this.activeTab === 'subscriptions' && !this.subsData.timeSeries) this.fetchSubscriptions()
    if (this.activeTab === 'ota' && !this.otaData.timeSeries) this.fetchOta()
    if (this.activeTab === 'otaVersions' && !this.otaVersionData.distribution) this.fetchOtaVersions()
    if (this.activeTab === 'downloads' && !this.downloadsData.google) this.fetchDownloads()
    if (this.activeTab === 'registrations' && !this.regData.rows?.length) this.fetchRegistrations()
  }

  private async exportToExcel() {
    this.exportLoading = true
    try {
      const params: any = { groupBy: 'month' }
      if (this.exportDateRange?.length === 2) {
        params.startDate = this.exportDateRange[0]
        params.endDate = this.exportDateRange[1]
      }

      const [devicesResp, activationResp, subsResp, ratingsResp, otaResp] = await Promise.all([
        request.get('/analytics/unique-connected-devices', { params }).catch(() => ({ data: { data: {} } })),
        request.get('/analytics/activation-success-rate', { params }).catch(() => ({ data: { data: {} } })),
        request.get('/analytics/new-subscriptions', { params: { ...params, platform: 'all' } }).catch(() => ({ data: { data: {} } })),
        request.get('/analytics/store-ratings').catch(() => ({ data: { data: {} } })),
        request.get('/analytics/ota-connection-ratio', { params }).catch(() => ({ data: { data: {} } }))
      ])

      const wb = XLSX.utils.book_new()

      // Sheet 1: Connected Devices
      const devData = devicesResp.data?.data || {}
      const devRows = (devData.timeSeries || []).map((r: any) => ({
        Period: this.formatPeriod(r.period, 'month'),
        V1: r.V1,
        V2: r.V2,
        Total: r.V1 + r.V2
      }))
      if (devData.summary) {
        devRows.push({ Period: 'TOTAL', V1: devData.summary.totalV1, V2: devData.summary.totalV2, Total: devData.summary.total })
      }
      const wsDevices = XLSX.utils.json_to_sheet(devRows.length ? devRows : [{ Period: 'No data' }])
      XLSX.utils.book_append_sheet(wb, wsDevices, 'Connected Devices')

      // Sheet 2: Activation Rate
      const actData = activationResp.data?.data || {}
      const actRows = (actData.timeSeries || []).map((r: any) => ({
        Period: this.formatPeriod(r.period, 'month'),
        'Total Activations': r.totalActivations,
        Completed: r.completed,
        'Success Rate (%)': r.successRate
      }))
      if (actData.summary) {
        actRows.push({ Period: 'TOTAL', 'Total Activations': actData.summary.totalActivations, Completed: actData.summary.totalCompleted, 'Success Rate (%)': actData.summary.overallSuccessRate })
      }
      const wsActivation = XLSX.utils.json_to_sheet(actRows.length ? actRows : [{ Period: 'No data' }])
      XLSX.utils.book_append_sheet(wb, wsActivation, 'Activation Rate')

      // Sheet 3: New Subscriptions
      const subData = subsResp.data?.data || {}
      const subRows = (subData.timeSeries || []).map((r: any) => {
        const row: any = {
          Period: this.formatPeriod(r.period, 'month'),
          Platform: r.platform,
          Product: r.productId,
          Type: r.productType || '',
          Count: r.count
        }
        if (r.revenueByCurrency?.length) {
          for (const rc of r.revenueByCurrency) {
            row[`Revenue (${rc.currency})`] = rc.amount
          }
        }
        return row
      })
      const wsSubs = XLSX.utils.json_to_sheet(subRows.length ? subRows : [{ Period: 'No data' }])
      XLSX.utils.book_append_sheet(wb, wsSubs, 'New Subscriptions')

      // Sheet 4: Store Ratings
      const ratData = ratingsResp.data?.data || {}
      const ratRows: any[] = []
      // Apple by country
      for (const c of (ratData.apple?.byCountry || [])) {
        ratRows.push({ Store: 'App Store', Country: c.country, Rating: c.rating, 'Rating Count': c.ratingCount })
      }
      // Google
      if (ratData.google?.globalRating) {
        ratRows.push({ Store: 'Google Play', Country: 'Global', Rating: ratData.google.globalRating, 'Rating Count': ratData.google.globalRatingCount })
      }
      const wsRatings = XLSX.utils.json_to_sheet(ratRows.length ? ratRows : [{ Store: 'No data' }])
      XLSX.utils.book_append_sheet(wb, wsRatings, 'Store Ratings')

      // Sheet 5: Reviews
      const reviewRows: any[] = []
      for (const r of (ratData.apple?.recentReviews || [])) {
        reviewRows.push({ Store: 'App Store', Rating: r.rating, Country: r.territory, Date: r.date?.substring(0, 10), User: r.reviewerNickname, Title: r.title, Review: r.body })
      }
      for (const r of (ratData.google?.recentReviews || [])) {
        reviewRows.push({ Store: 'Google Play', Rating: r.rating, Country: '', Date: r.date?.substring(0, 10), User: r.reviewerNickname, Title: '', Review: r.text })
      }
      reviewRows.sort((a: any, b: any) => (b.Date || '').localeCompare(a.Date || ''))
      const wsReviews = XLSX.utils.json_to_sheet(reviewRows.length ? reviewRows : [{ Store: 'No data' }])
      XLSX.utils.book_append_sheet(wb, wsReviews, 'Reviews')

      // Sheet 6: OTA vs Connections
      const otaD = otaResp.data?.data || {}
      const otaRows = (otaD.timeSeries || []).map((r: any) => ({
        Period: this.formatPeriod(r.period, 'month'),
        'OTA Users': r.otaCount,
        'New Connections (Pair)': r.newConnections,
        'Adoption Rate (%)': r.adoptionRate
      }))
      if (otaD.summary) {
        otaRows.push({ Period: 'TOTAL', 'OTA Users': otaD.summary.totalOta, 'New Connections (Pair)': otaD.summary.totalNewConnections, 'Adoption Rate (%)': otaD.summary.overallAdoptionRate })
      }
      const wsOta = XLSX.utils.json_to_sheet(otaRows.length ? otaRows : [{ Period: 'No data' }])
      XLSX.utils.book_append_sheet(wb, wsOta, 'OTA vs Connections')

      // Auto-width for all sheets
      for (const name of wb.SheetNames) {
        const ws = wb.Sheets[name]
        const data = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 })
        if (data.length) {
          ws['!cols'] = (data[0] as any[]).map((_: any, i: number) => {
            let maxLen = 10
            for (const row of data as any[][]) {
              const val = row[i]
              if (val != null) maxLen = Math.max(maxLen, String(val).length)
            }
            return { wch: Math.min(maxLen + 2, 50) }
          })
        }
      }

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })
      const buf = new ArrayBuffer(wbout.length)
      const view = new Uint8Array(buf)
      for (let i = 0; i < wbout.length; i++) view[i] = wbout.charCodeAt(i) & 0xFF

      const dateStr = this.exportDateRange?.length === 2 ? `_${this.exportDateRange[0]}_${this.exportDateRange[1]}` : ''
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `KPI_Analytics${dateStr}.xlsx`)
    } catch (e) {
      console.error('Export failed:', e)
      this.$message.error('Export failed. Please try again.')
    }
    this.exportLoading = false
  }
}
